import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { TenancyFormModal } from "./tenancy-form-modal";

interface PropertyTenancy {
  id: string;
  securityPropertyId: string;
  tenantName: string;
  tenantType: string;
  tenantAbn: string | null;
  leaseStartDate: string;
  leaseEndDate: string;
  currentRentAnnual: string;
  tenancyAreaSqm: string | null;
  isVacant: boolean | null;
}

interface SecurityTenanciesTabProps {
  securityId: string;
}

// Tenant type labels for user-friendly display
const TENANT_TYPE_LABELS: Record<string, string> = {
  federal_government: "Federal Government",
  state_government: "State Government",
  local_government: "Local Government",
  listed_public_company: "Listed Public Company",
  private_company: "Private Company",
  multinational_corporation: "Multinational Corporation",
  franchise_national: "Franchise (National)",
  franchise_local: "Franchise (Local)",
  national_retailer: "National Retailer",
  major_bank: "Major Bank",
  supermarket_chain: "Supermarket Chain",
  fast_food_chain: "Fast Food Chain",
  restaurant_cafe: "Restaurant/Cafe",
  hotel_motel: "Hotel/Motel",
  medical_health: "Medical/Health",
  legal_accounting: "Legal/Accounting",
  real_estate_agency: "Real Estate Agency",
  child_care_centre: "Child Care Centre",
  manufacturing: "Manufacturing",
  warehousing_logistics: "Warehousing/Logistics",
  automotive: "Automotive",
  sme_business: "SME Business",
  sole_trader: "Sole Trader",
  residential_mixed_use: "Residential Mixed Use",
  vacant: "Vacant",
  owner_occupied: "Owner Occupied",
  corporate: "Corporate",
  individual: "Individual",
};

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatArea(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return `${num.toLocaleString("en-AU")} sqm`;
}

function getTenantTypeLabel(type: string): string {
  return TENANT_TYPE_LABELS[type] || type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

export function SecurityTenanciesTab({ securityId }: SecurityTenanciesTabProps) {
  const { toast } = useToast();
  const [tenancyModalOpen, setTenancyModalOpen] = useState(false);
  const [editingTenancy, setEditingTenancy] = useState<PropertyTenancy | null>(null);
  const [deleteTenancyDialog, setDeleteTenancyDialog] = useState<{
    open: boolean;
    tenancy: PropertyTenancy | null;
  }>({
    open: false,
    tenancy: null,
  });

  // Fetch tenancies
  const { data: tenanciesData, isLoading } = useQuery<PropertyTenancy[]>({
    queryKey: [`/api/crm/securities/${securityId}/tenancies`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/securities/${securityId}/tenancies`);
      return response.json();
    },
    enabled: !!securityId,
  });

  const tenancies = Array.isArray(tenanciesData) ? tenanciesData : [];

  // Delete mutation
  const deleteTenancyMutation = useMutation({
    mutationFn: async (tenancyId: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/crm/securities/${securityId}/tenancies/${tenancyId}`
      );
      if (!response.ok) {
        throw new Error("Failed to delete tenancy");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/crm/securities/${securityId}/tenancies`],
      });
      toast({
        description: "Tenancy deleted",
      });
      setDeleteTenancyDialog({ open: false, tenancy: null });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEditTenancy = (tenancy: PropertyTenancy) => {
    setEditingTenancy(tenancy);
    setTenancyModalOpen(true);
  };

  const handleTenancyModalClose = (open: boolean) => {
    setTenancyModalOpen(open);
    if (!open) setEditingTenancy(null);
  };

  const handleAddTenancy = () => {
    setEditingTenancy(null);
    setTenancyModalOpen(true);
  };

  // Loading state with skeleton rows
  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold">Tenancies</h3>
            <Button size="sm" variant="outline" disabled>
              <Plus className="h-4 w-4 mr-1" />
              Add Tenancy
            </Button>
          </div>
          <div data-testid="loading-skeleton" className="space-y-2">
            <Skeleton className="h-10 w-full animate-pulse" />
            <Skeleton className="h-10 w-full animate-pulse" />
            <Skeleton className="h-10 w-full animate-pulse" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (tenancies.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold">Tenancies</h3>
            </div>
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No tenancies recorded</p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddTenancy}
                data-testid="tenancy-add-button"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Tenancy
              </Button>
            </div>
          </CardContent>
        </Card>

        <TenancyFormModal
          open={tenancyModalOpen}
          onOpenChange={handleTenancyModalClose}
          securityId={securityId}
          tenancy={editingTenancy}
        />
      </>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold">Tenancies</h3>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddTenancy}
              data-testid="tenancy-add-button"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Tenancy
            </Button>
          </div>

          <div data-testid="tenancies-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Lease Start</TableHead>
                  <TableHead>Lease End</TableHead>
                  <TableHead>Rent p.a.</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenancies.map((tenancy) => {
                  const isVacant = tenancy.isVacant || tenancy.tenantType === "vacant";
                  return (
                    <TableRow
                      key={tenancy.id}
                      className={isVacant ? "italic text-muted-foreground" : ""}
                    >
                      <TableCell className={isVacant ? "italic text-muted-foreground" : ""}>
                        {isVacant ? "[VACANT]" : tenancy.tenantName}
                      </TableCell>
                      <TableCell>
                        {getTenantTypeLabel(tenancy.tenantType)}
                      </TableCell>
                      <TableCell>
                        {isVacant ? "-" : formatDate(tenancy.leaseStartDate)}
                      </TableCell>
                      <TableCell data-testid="lease-end-cell">
                        {isVacant ? "-" : formatDate(tenancy.leaseEndDate)}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(tenancy.currentRentAnnual)}
                      </TableCell>
                      <TableCell>
                        {formatArea(tenancy.tenancyAreaSqm)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditTenancy(tenancy)}
                          data-testid="tenancy-edit-button"
                          aria-label="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() =>
                            setDeleteTenancyDialog({ open: true, tenancy })
                          }
                          data-testid="tenancy-delete-button"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <TenancyFormModal
        open={tenancyModalOpen}
        onOpenChange={handleTenancyModalClose}
        securityId={securityId}
        tenancy={editingTenancy}
      />

      <AlertDialog
        open={deleteTenancyDialog.open}
        onOpenChange={(open) =>
          setDeleteTenancyDialog({
            open,
            tenancy: open ? deleteTenancyDialog.tenancy : null,
          })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Tenancy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this tenancy? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteTenancyDialog.tenancy &&
                deleteTenancyMutation.mutate(deleteTenancyDialog.tenancy.id)
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="tenancy-delete-confirm-button"
            >
              {deleteTenancyMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
